import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { useHost } from '../contexts/HostContext';
import { getRequiredConfig } from '../lib/service-discovery/bootstrap';
import { McpSession, getOrCreateDeviceId } from '../services/mcp/mcp-session';
export function useMcpSession() {
    const { user, isAuthenticated } = useAuth();
    const { hostType } = useHost();
    const sessionRef = useRef(null);
    const [sessionId, setSessionId] = useState(null);
    const [connectionStatus, setConnectionStatus] = useState(null);
    const [connectionError, setConnectionError] = useState(null);
    const deviceId = useMemo(() => getOrCreateDeviceId(hostType), [hostType]);
    // Track the latest access token in a ref so the factory can use it as a
    // fallback without depending on user?.access_token in the effect deps
    const latestTokenRef = useRef(null);
    if (user?.access_token) {
        latestTokenRef.current = user.access_token;
    }
    const onSessionIdChanged = useCallback((id) => {
        setSessionId(id);
    }, []);
    const onConnectionStatusChanged = useCallback((status, error) => {
        setConnectionStatus(status);
        setConnectionError(status === 'disconnected' || status === 'reconnecting' ? (error ?? null) : null);
    }, []);
    useEffect(() => {
        if (!isAuthenticated || !latestTokenRef.current)
            return;
        let cancelled = false;
        const config = getRequiredConfig();
        const hubUrl = config.hubUrl;
        const accessTokenFactory = async () => {
            // Read fresh token from the OIDC storage
            const storageKey = `oidc.user:${config.authority}:${config.clientId}`;
            const stored = sessionStorage.getItem(storageKey) || localStorage.getItem(storageKey);
            if (stored) {
                try {
                    const parsed = JSON.parse(stored);
                    if (parsed.access_token)
                        return parsed.access_token;
                }
                catch {
                    // Fall through to ref token
                }
            }
            return latestTokenRef.current ?? '';
        };
        const session = new McpSession(hostType, hubUrl, accessTokenFactory, onSessionIdChanged, (status, error) => {
            if (!cancelled)
                onConnectionStatusChanged(status, error);
        });
        sessionRef.current = session;
        session.start().catch((error) => {
            console.error('[useMcpSession] Failed to start MCP session:', error);
        });
        return () => {
            cancelled = true;
            session.stop();
            sessionRef.current = null;
        };
    }, [isAuthenticated, hostType, onSessionIdChanged, onConnectionStatusChanged]);
    return { sessionId, deviceId, connectionStatus, connectionError };
}
